import React, { useMemo, useState } from 'react'
import type { Task, TaskStatus } from '../../types/mockup'

interface NowNextLaterProps {
  tasks: Task[]
  currentTime?: Date
}

interface TaskGroup {
  now: Task[]
  next: Task[]
  later: Task[]
}

type StatusFilter = 'all' | 'complete' | 'remaining' | 'at_risk' | 'delayed'

export const NowNextLater: React.FC<NowNextLaterProps> = ({ 
  tasks, 
  currentTime = new Date() 
}) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  
  // Filter tasks based on selected status
  const filteredByStatus = useMemo(() => {
    switch (statusFilter) {
      case 'complete':
        return tasks.filter(t => t.status === 'complete')
      case 'remaining':
        return tasks.filter(t => t.status !== 'complete')
      case 'at_risk':
        return tasks.filter(t => t.status === 'at_risk')
      case 'delayed':
        return tasks.filter(t => t.status === 'delayed')
      default:
        return tasks
    }
  }, [tasks, statusFilter])
  
  const groupedTasks = useMemo<TaskGroup>(() => {
    const sortedTasks = [...filteredByStatus]
      .filter(task => task.status !== 'complete' || statusFilter === 'complete')
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())

    const now: Task[] = []
    const next: Task[] = []
    const later: Task[] = []

    // Special handling for completed filter - show recent completed tasks
    if (statusFilter === 'complete') {
      // Show the most recent completed tasks regardless of time
      const recentCompleted = sortedTasks.slice(-8).reverse() // Get last 8 completed tasks
      recentCompleted.forEach((task, index) => {
        if (index < 3) now.push(task)
        else if (index < 6) next.push(task)
        else later.push(task)
      })
    } else {
      sortedTasks.forEach(task => {
        const taskStart = task.startTime.getTime()
        const taskEnd = task.endTime.getTime()
        const current = currentTime.getTime()
        
        if (taskStart <= current && taskEnd >= current) {
          now.push(task)
        } else if (taskStart > current && taskStart <= current + 60 * 60 * 1000) {
          next.push(task)
        } else if (taskStart > current + 60 * 60 * 1000 && taskStart <= current + 3 * 60 * 60 * 1000) {
          later.push(task)
        }
      })
    }

    return { now, next, later }
  }, [filteredByStatus, currentTime, statusFilter])

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const getMinutesUntil = (task: Task): string => {
    const minutes = Math.floor((task.startTime.getTime() - currentTime.getTime()) / 60000)
    if (minutes < 0) return 'Started'
    if (minutes === 0) return 'Starting now'
    if (minutes === 1) return 'In 1 minute'
    return `In ${minutes} minutes`
  }

  const getStatusColor = (status: Task['status']): string => {
    switch (status) {
      case 'in_progress': return '#4ECDC4'
      case 'at_risk': return '#FFB347'
      case 'delayed': return '#FF6B6B'
      case 'pending': return '#95A5A6'
      default: return '#95A5A6'
    }
  }

  const renderTaskSummary = (task: Task, showTiming: boolean = true) => (
    <div key={task.id} className="now-next-later-task">
      <div className="task-summary-header">
        <span className="task-summary-name">{task.name}</span>
        {task.isAnchor && (
          <span className="anchor-indicator" title="Anchor task - cannot be moved">🔒</span>
        )}
      </div>
      <div className="task-summary-details">
        <span className="task-time">{formatTime(task.startTime)}</span>
        {task.supplier && (
          <span className="task-supplier" style={{ color: task.supplier.color }}>
            {task.supplier.name}
          </span>
        )}
        {showTiming && task.startTime > currentTime && (
          <span className="task-timing">{getMinutesUntil(task)}</span>
        )}
      </div>
      <div 
        className="task-status-indicator" 
        style={{ backgroundColor: getStatusColor(task.status) }}
      />
    </div>
  )

  const hasActiveTasks = groupedTasks.now.length > 0
  const hasUpcomingTasks = groupedTasks.next.length > 0 || groupedTasks.later.length > 0

  return (
    <div className="now-next-later-container">
      <div className="now-next-later-header">
        <h3>Current Status</h3>
        <span className="current-time">{formatTime(currentTime)}</span>
      </div>

      <div className="summary-stats">
        <button
          className="stat-item"
          onClick={() => setStatusFilter(statusFilter === 'complete' ? 'all' : 'complete')}
          style={{
            cursor: 'pointer',
            background: statusFilter === 'complete' ? '#e7f5f5' : 'transparent',
            border: statusFilter === 'complete' ? '2px solid #4ECDC4' : '1px solid transparent',
            borderRadius: '8px',
            padding: '8px 12px',
            transition: 'all 0.2s'
          }}
        >
          <span className="stat-label">Completed</span>
          <span className="stat-value" style={{ color: '#4ECDC4' }}>
            {tasks.filter(t => t.status === 'complete').length}
          </span>
        </button>
        <button
          className="stat-item"
          onClick={() => setStatusFilter(statusFilter === 'remaining' ? 'all' : 'remaining')}
          style={{
            cursor: 'pointer',
            background: statusFilter === 'remaining' ? '#f0f4f8' : 'transparent',
            border: statusFilter === 'remaining' ? '2px solid #5f6368' : '1px solid transparent',
            borderRadius: '8px',
            padding: '8px 12px',
            transition: 'all 0.2s'
          }}
        >
          <span className="stat-label">Remaining</span>
          <span className="stat-value">
            {tasks.filter(t => t.status !== 'complete').length}
          </span>
        </button>
        <button
          className="stat-item"
          onClick={() => setStatusFilter(statusFilter === 'at_risk' ? 'all' : 'at_risk')}
          style={{
            cursor: 'pointer',
            background: statusFilter === 'at_risk' ? '#fff4e6' : 'transparent',
            border: statusFilter === 'at_risk' ? '2px solid #FFB347' : '1px solid transparent',
            borderRadius: '8px',
            padding: '8px 12px',
            transition: 'all 0.2s'
          }}
        >
          <span className="stat-label">At Risk</span>
          <span className="stat-value" style={{ color: '#FFB347' }}>
            {tasks.filter(t => t.status === 'at_risk').length}
          </span>
        </button>
        <button
          className="stat-item"
          onClick={() => setStatusFilter(statusFilter === 'delayed' ? 'all' : 'delayed')}
          style={{
            cursor: 'pointer',
            background: statusFilter === 'delayed' ? '#ffe6e6' : 'transparent',
            border: statusFilter === 'delayed' ? '2px solid #FF6B6B' : '1px solid transparent',
            borderRadius: '8px',
            padding: '8px 12px',
            transition: 'all 0.2s'
          }}
        >
          <span className="stat-label">Delayed</span>
          <span className="stat-value" style={{ color: '#FF6B6B' }}>
            {tasks.filter(t => t.status === 'delayed').length}
          </span>
        </button>
      </div>

      <div className="now-section">
        <h4 className="section-title">
          <span className="section-icon">▶</span>
          {statusFilter === 'complete' ? 'Recently Completed' : 'Now'}
        </h4>
        <div className="section-content">
          {hasActiveTasks || (statusFilter === 'complete' && groupedTasks.now.length > 0) ? (
            groupedTasks.now.map(task => renderTaskSummary(task, false))
          ) : (
            <div className="empty-state">
              {statusFilter === 'complete' ? 'No recently completed tasks' : 'No active tasks'}
            </div>
          )}
        </div>
      </div>

      <div className="next-section">
        <h4 className="section-title">
          <span className="section-icon">⏭</span>
          {statusFilter === 'complete' ? 'Earlier Today' : 'Next (within 1 hour)'}
        </h4>
        <div className="section-content">
          {groupedTasks.next.length > 0 ? (
            groupedTasks.next.slice(0, 3).map(task => renderTaskSummary(task))
          ) : (
            <div className="empty-state">
              {statusFilter === 'complete' ? 'No completed tasks' : 'No tasks in the next hour'}
            </div>
          )}
        </div>
      </div>

      <div className="later-section">
        <h4 className="section-title">
          <span className="section-icon">⏱</span>
          {statusFilter === 'complete' ? 'Previous Tasks' : 'Later (1-3 hours)'}
        </h4>
        <div className="section-content">
          {groupedTasks.later.length > 0 ? (
            groupedTasks.later.slice(0, 2).map(task => renderTaskSummary(task))
          ) : (
            <div className="empty-state">
              {statusFilter === 'complete' ? 'No previous tasks' : 'No tasks scheduled'}
            </div>
          )}
        </div>
      </div>

      {!hasActiveTasks && !hasUpcomingTasks && (
        <div className="all-clear-message">
          <span className="all-clear-icon">✅</span>
          <p>All tasks completed or no upcoming tasks</p>
        </div>
      )}
    </div>
  )
}